import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';
import { CONTENT_TYPES, SOCIAL_PLATFORMS } from '~/constants';
import {
  CONTENTS_COLLECTION_QUERY_KEY,
  MEDIA_PACK_ENTITY_QUERY_KEY,
} from '~/queries/keys';
import {
  Content,
  CreatorBenchmarks,
  DefaultMediaPackType,
  InstagramInsightType,
  PostContent,
  ReelsContent,
  StoryContent,
} from '~/types';
import { isPostContent, isReelContent, isStoryContent } from '~/utils';

import { patchMediaPack } from '../../MediaPackDetailDefaultScreen.services';
import { InstagramSection } from './InstagramSection';
import { mediaPackContentToIds } from './InstagramSection.mappers';
import { getInstagramContents } from './InstagramSection.services';

interface Props {
  mediaPack: DefaultMediaPackType;
  creatorBenchmarks: CreatorBenchmarks;
  creatorInsights: InstagramInsightType;
  isSharedView?: boolean;
}

const MAX_NUMBER_OF_POSTS = 6;
const MAX_NUMBER_OF_STORIES = 6;
const MAX_NUMBER_OF_REELS = 6;

export const InstagramSectionContainer: React.FC<Props> = ({
  creatorBenchmarks,
  creatorInsights,
  mediaPack,
  isSharedView,
}) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { sharedToken, creatorSlug } = router.query;
  const contentIds = mediaPackContentToIds(mediaPack?.contents);

  const {
    data: contents = [],
    isFetching,
    error,
  } = useQuery<Content[], AxiosError>(
    [CONTENTS_COLLECTION_QUERY_KEY, contentIds],
    () => getInstagramContents(contentIds, sharedToken as string),
    {
      enabled: mediaPack?.contents?.length > 0,
      keepPreviousData: true,
    },
  );

  const igPosts = contents.filter(isPostContent) as PostContent[];
  const topSixPosts = igPosts.slice(0, MAX_NUMBER_OF_POSTS);
  const igStories = contents.filter(isStoryContent) as StoryContent[];
  const topSixStories = igStories.slice(0, MAX_NUMBER_OF_STORIES);
  const igReels = contents.filter(isReelContent) as ReelsContent[];
  const topSixReels = igReels.slice(0, MAX_NUMBER_OF_REELS);

  const handleUpdateMediaPack = async (payload: {
    contents: any[];
    selectedContentType: 'post' | 'story' | 'reels';
    platform: SOCIAL_PLATFORMS;
  }) => {
    const contentData: {
      post: PostContent[];
      story: StoryContent[];
      reels: ReelsContent[];
    } = {
      [CONTENT_TYPES.post]: igPosts,
      [CONTENT_TYPES.story]: igStories,
      [CONTENT_TYPES.reels]: igReels,
    };

    // Override contentData with new contents selected
    contentData[payload.selectedContentType] = payload.contents;

    // Merge all contents in an array for send in patchMediaPack
    const submitData = [
      ...contentData.post,
      ...contentData.story,
      ...contentData.reels,
    ];

    await patchMediaPack({
      id: mediaPack?.id,
      data: {
        contents: submitData,
      },
      _impersonateOnce: creatorSlug as string,
    });

    await queryClient.invalidateQueries({
      queryKey: [MEDIA_PACK_ENTITY_QUERY_KEY],
    });
    await queryClient.invalidateQueries({
      queryKey: [CONTENTS_COLLECTION_QUERY_KEY],
    });
  };

  return (
    <InstagramSection
      mediaPack={mediaPack}
      creatorBenchmarks={creatorBenchmarks}
      creatorInsights={creatorInsights}
      igPosts={topSixPosts}
      igStories={topSixStories}
      igReels={topSixReels}
      onUpdateMediaPack={handleUpdateMediaPack}
      isSharedView={isSharedView}
      isLoading={isFetching}
      error={error}
    />
  );
};
